/*
 * Tencent is pleased to support the open source community by making
 * 蓝鲸智云PaaS平台 (BlueKing PaaS) available.
 *
 * Copyright (C) 2021 THL A29 Limited, a Tencent company.  All rights reserved.
 *
 * 蓝鲸智云PaaS平台 (BlueKing PaaS) is licensed under the MIT License.
 *
 * License for 蓝鲸智云PaaS平台 (BlueKing PaaS):
 *
 * ---------------------------------------------------
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
 * documentation files (the "Software"), to deal in the Software without restriction, including without limitation
 * the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and
 * to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of
 * the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
 * THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF
 * CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */
import { defineComponent, h } from 'vue';

import { commonSVGProps } from '../common/icon';

let _svgUid = 0;

function uniqueGradientIds(prefix: string) {
  const base = `${prefix}-${++_svgUid}`;
  return {
    id: (n: number) => `${base}-${n}`,
    ref: (n: number) => `url(#${base}-${n})`,
  };
}

export const AIBluekingIcon = defineComponent({
  name: 'AIBluekingIcon',
  setup() {
    const g = uniqueGradientIds('blueking-icon');
    return () =>
      h(
        'svg',
        {
          ...commonSVGProps,
          viewBox: '0 0 24 24',
          class: {
            [commonSVGProps.class]: true,
            'ai-blueking-icon': true,
          },
        },
        [
          h('defs', {}, [
            h(
              'linearGradient',
              {
                x1: '45.02491%',
                y1: '35.5927467%',
                x2: '99.2803929%',
                y2: '81.0358394%',
                id: g.id(1),
              },
              [
                h('stop', { 'stop-color': '#235DFA', offset: '0%' }),
                h('stop', { 'stop-color': '#EB8CEC', offset: '100%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '50%',
                y1: '0%',
                x2: '50%',
                y2: '100%',
                id: g.id(2),
              },
              [
                h('stop', { 'stop-color': '#FFFFFF', 'stop-opacity': '0.885701426', offset: '0%' }),
                h('stop', { 'stop-color': '#FFFFFF', 'stop-opacity': '0.29', offset: '100%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '50%',
                y1: '0%',
                x2: '50%',
                y2: '100%',
                id: g.id(3),
              },
              [
                h('stop', { 'stop-color': '#1562FC', offset: '0%' }),
                h('stop', { 'stop-color': '#EB8CEC', offset: '100%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '50%',
                y1: '95.0902604%',
                x2: '35.112422%',
                y2: '19.6450217%',
                id: g.id(4),
              },
              [
                h('stop', { 'stop-color': '#DC63FE', offset: '0.0150240385%' }),
                h('stop', { 'stop-color': '#235DFA', offset: '100%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '46.6072083%',
                y1: '95.8855311%',
                x2: '46.6072083%',
                y2: '50%',
                id: g.id(5),
              },
              [
                h('stop', { 'stop-color': '#B962FD', offset: '0%' }),
                h('stop', { 'stop-color': '#565FFB', offset: '99.984976%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '50%',
                y1: '0%',
                x2: '50%',
                y2: '100%',
                id: g.id(6),
              },
              [
                h('stop', { 'stop-color': '#235DFA', offset: '0%' }),
                h('stop', { 'stop-color': '#A826E2', offset: '100%' }),
              ],
            ),
          ]),
          h(
            'g',
            {
              stroke: 'none',
              'stroke-width': '1',
              fill: 'none',
              'fill-rule': 'evenodd',
            },
            [
              h(
                'g',
                {
                  transform: 'translate(-1940, -1102)',
                },
                [
                  h(
                    'g',
                    {
                      transform: 'translate(1941, 1103.2578)',
                    },
                    [
                      h('path', {
                        d: 'M9.5,4.7422389 C13.6421356,4.7422389 17,8.10010327 17,12.2422389 L17.001443,17.8260065 C18.766577,18.002886 20,18.503758 20,19.7276221 C19.9911111,19.736626 19.9733333,19.7388478 19.92,19.740251 L19.8835437,19.7409966 C19.8766933,19.7411051 19.86944,19.7412059 19.86176,19.7412992 L19.223277,19.7414596 C19.1990044,19.7413893 19.1739733,19.7413146 19.14816,19.7412361 L18.89408,19.7404278 C18.86336,19.7403275 18.8317867,19.7402239 18.7993363,19.7401172 L18.59392,19.7394428 C18.5578667,19.7393249 18.5208889,19.7392045 18.482963,19.7390817 L18.24384,19.7383188 C18.1184,19.7379252 17.984,19.7375147 17.84,19.7370938 L17.5390104,19.7362397 C17.371281,19.7357782 17.19212,19.7353095 17.0007483,19.7348414 L9.5,19.7422389 C6.21251981,19.7422389 3.41905896,17.6270907 2.40625547,14.6834322 C1.03363082,14.4093871 0,13.203242 0,11.7568557 L1.21619234,11.7563718 C1.49913049,11.7561381 1.76533428,11.755827 2.01579346,11.7554549 C2.26623611,7.84031745 5.52132054,4.7422389 9.5,4.7422389 Z',
                        fill: g.ref(1),
                      }),
                      h('path', {
                        d: 'M9.5,6.7422389 C12.5375661,6.7422389 15,9.20467277 15,12.2422389 L15,15.7422389 C15,16.8468084 14.1045695,17.7422389 13,17.7422389 L9.5,17.7422389 C6.46243388,17.7422389 4,15.279805 4,12.2422389 C4,9.20467277 6.46243388,6.7422389 9.5,6.7422389 Z',
                        fill: g.ref(2),
                      }),
                      h('rect', {
                        fill: g.ref(3),
                        x: '18',
                        y: '7.7422389',
                        width: '2',
                        height: '9',
                        rx: '1',
                      }),
                      h('path', {
                        d: 'M18.9843802,1 C18.9843802,2.52940392 20.1288357,3.79150593 21.6080665,3.97662574 L21.9843802,4 C20.4549762,4 19.1928742,5.14445555 19.0077544,6.62368637 L18.9843802,7 C18.9843802,5.47059608 17.8399246,4.20849407 16.3606938,4.02337426 L15.9843802,4 C17.5137841,4 18.7758861,2.85554445 18.9610059,1.37631363 L18.9843802,1 Z',
                        fill: g.ref(4),
                        'fill-rule': 'nonzero',
                      }),
                      h('path', {
                        d: 'M14.9843802,-1.0658141e-14 C14.9843802,1.01960262 15.7473505,1.86100395 16.7335044,1.98441716 L16.9843802,2 C15.9647776,2 15.1233762,2.76297037 14.999963,3.74912425 L14.9843802,4 C14.9843802,2.98039738 14.2214098,2.13899605 13.2352559,2.01558284 L12.9843802,2 C14.0039828,2 14.8453841,1.23702963 14.9687973,0.250875751 L14.9843802,-1.0658141e-14 Z',
                        fill: g.ref(5),
                        'fill-rule': 'nonzero',
                      }),
                      h('path', {
                        d: 'M15.9843802,5 C15.9843802,5.50980131 16.3658654,5.93050198 16.8589423,5.99220858 L16.9843802,6 C16.4745789,6 16.0538782,6.38148518 15.9921716,6.87456212 L15.9843802,7 C15.9843802,6.49019869 15.602895,6.06949802 15.109818,6.00779142 L14.9843802,6 C15.4941815,6 15.9148821,5.61851482 15.9765887,5.12543788 L15.9843802,5 Z',
                        fill: '#8860FC',
                        'fill-rule': 'nonzero',
                      }),
                      h('rect', {
                        fill: '#16204D',
                        x: '6.5',
                        y: '10.7422389',
                        width: '2',
                        height: '3',
                        rx: '1',
                      }),
                      h('rect', {
                        fill: '#16204D',
                        x: '10.5',
                        y: '10.7422389',
                        width: '2',
                        height: '3',
                        rx: '1',
                      }),
                      h('path', {
                        d: 'M12.1896605,4.7422389 C11.9209486,3.40890556 11.3915034,2.7422389 10.6013249,2.7422389 C9.41605731,2.7422389 9.42887356,4.32659654 10.6013249,4.18645894 C11.2198089,4.11253447 11.6391525,4.29779445 11.8593557,4.7422389 L12.1896605,4.7422389 Z',
                        fill: g.ref(6),
                        transform: 'translate(10.9534, 3.7422) scale(-1, 1) translate(-10.9534, -3.7422)',
                      }),
                      h('path', {
                        d: 'M9.71717378,4.7422389 C9.4484618,3.40890556 8.9190166,2.7422389 8.12883818,2.7422389 C6.94357055,2.7422389 6.95638679,4.32659654 8.12883818,4.18645894 C8.74732212,4.11253447 9.16666571,4.29779445 9.38686894,4.7422389 L9.71717378,4.7422389 Z',
                        fill: g.ref(6),
                      }),
                    ],
                  ),
                ],
              ),
            ],
          ),
        ],
      );
  },
});

export const AIBluekingBannerIcon = defineComponent({
  name: 'AIBluekingBannerIcon',
  setup() {
    const g = uniqueGradientIds('banner-icon');
    return () =>
      h(
        'svg',
        {
          ...commonSVGProps,
          viewBox: '0 0 309 93',
          class: {
            [commonSVGProps.class]: true,
            'ai-blueking-banner-icon': true,
          },
        },
        [
          h('defs', {}, [
            h(
              'linearGradient',
              {
                x1: '45.02491%',
                y1: '35.5927467%',
                x2: '99.2803929%',
                y2: '81.0358394%',
                id: g.id(1),
              },
              [
                h('stop', { 'stop-color': '#235DFA', offset: '0%' }),
                h('stop', { 'stop-color': '#EB8CEC', offset: '100%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '50%',
                y1: '0%',
                x2: '50%',
                y2: '100%',
                id: g.id(2),
              },
              [
                h('stop', { 'stop-color': '#FFFFFF', 'stop-opacity': '0.885701426', offset: '0%' }),
                h('stop', { 'stop-color': '#FFFFFF', 'stop-opacity': '0.29', offset: '100%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '50%',
                y1: '0%',
                x2: '50%',
                y2: '100%',
                id: g.id(3),
              },
              [
                h('stop', { 'stop-color': '#1562FC', offset: '0%' }),
                h('stop', { 'stop-color': '#EB8CEC', offset: '100%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '50%',
                y1: '95.0902604%',
                x2: '35.112422%',
                y2: '19.6450217%',
                id: g.id(4),
              },
              [
                h('stop', { 'stop-color': '#DC63FE', offset: '0.0150240385%' }),
                h('stop', { 'stop-color': '#235DFA', offset: '100%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '46.6072083%',
                y1: '95.8855311%',
                x2: '46.6072083%',
                y2: '50%',
                id: g.id(5),
              },
              [
                h('stop', { 'stop-color': '#B962FD', offset: '0%' }),
                h('stop', { 'stop-color': '#565FFB', offset: '99.984976%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '50%',
                y1: '0%',
                x2: '50%',
                y2: '100%',
                id: g.id(6),
              },
              [
                h('stop', { 'stop-color': '#235DFA', offset: '0%' }),
                h('stop', { 'stop-color': '#A826E2', offset: '100%' }),
              ],
            ),
            h(
              'linearGradient',
              {
                x1: '50%',
                y1: '-38.6691405%',
                x2: '50%',
                y2: '100%',
                id: g.id(7),
              },
              [
                h('stop', { 'stop-color': '#235DFA', offset: '0%' }),
                h('stop', { 'stop-color': '#9F7BF1', 'stop-opacity': '0.15', offset: '62.335824%' }),
                h('stop', { 'stop-color': '#EB8CEC', 'stop-opacity': '0', offset: '100%' }),
              ],
            ),
          ]),
          h(
            'g',
            {
              stroke: 'none',
              'stroke-width': '1',
              fill: 'none',
              'fill-rule': 'evenodd',
            },
            [
              h(
                'g',
                {
                  transform: 'translate(0, 0.2974)',
                },
                [
                  h(
                    'g',
                    {
                      transform: 'translate(124.4167, 0)',
                    },
                    [
                      h('path', {
                        d: 'M28.3965776,12.9425679 C38.3377031,12.9425679 46.3965776,21.0014424 46.3965776,30.9425679 L46.3969075,48.6505618 C51.2162571,49.132982 54.5842089,50.4998157 54.5842089,53.8408323 C54.5842089,53.8444226 54.5842089,53.8477257 54.5824622,53.8507588 L54.5702354,53.8590653 C54.5370482,53.8709895 54.4250414,53.8772107 54.0470184,53.8795761 L53.8473221,53.8804155 C53.8227592,53.8804783 53.7973775,53.8805312 53.7711498,53.8805746 L53.4140599,53.8806511 C53.3806271,53.8806219 53.3462391,53.8805842 53.3108685,53.8805382 L52.8375142,53.8796076 C51.6610285,53.8767057 49.6921211,53.8685916 46.3970568,53.8605313 L46.3965776,53.8807246 L23.4584209,53.8807246 C14.9044209,53.8807246 7.74404265,47.9139206 5.91235374,39.9153803 C2.4976649,38.935189 0,35.8013205 0,32.0869334 L1.43000256,32.0866983 C1.58407741,32.0866469 1.73656386,32.0865873 1.88747828,32.0865198 L3.20370552,32.0857063 C4.00314289,32.0850715 4.75405903,32.0842107 5.4593989,32.0831725 L5.45842089,30.9425679 C5.45842089,21.0014424 13.5172954,12.9425679 23.4584209,12.9425679 L28.3965776,12.9425679 Z',
                        fill: g.ref(1),
                      }),
                      h('rect', {
                        fill: g.ref(2),
                        x: '10.9168418',
                        y: '18.4009888',
                        width: '30.0213149',
                        height: '30.0213149',
                        rx: '12',
                      }),
                      h('rect', {
                        fill: g.ref(3),
                        x: '49.125788',
                        y: '21.1301993',
                        width: '5.45842089',
                        height: '24.562894',
                        rx: '2',
                      }),
                      h('path', {
                        d: 'M51.8123687,2.72921045 C51.8123687,6.90327561 54.9358287,10.3478176 58.9729609,10.8530485 L60,10.9168418 C55.8259348,10.9168418 52.3813929,14.0403018 51.8761619,18.077434 L51.8123687,19.1044731 C51.8123687,14.930408 48.6889086,11.485866 44.6517764,10.9806351 L43.6247373,10.9168418 C47.7988025,10.9168418 51.2433445,7.79338174 51.7485754,3.75624952 L51.8123687,2.72921045 Z',
                        fill: g.ref(4),
                        'fill-rule': 'nonzero',
                      }),
                      h('path', {
                        d: 'M40.8955269,0 C40.8955269,2.78271011 42.9778336,5.07907143 45.6692551,5.41589204 L46.3539478,5.45842089 C43.5712377,5.45842089 41.2748763,7.54072759 40.9380557,10.2321491 L40.8955269,10.9168418 C40.8955269,8.13413168 38.8132202,5.83777036 36.1217987,5.50094974 L35.437106,5.45842089 C38.2198161,5.45842089 40.5161774,3.3761142 40.852998,0.684692719 L40.8955269,0 Z',
                        fill: g.ref(5),
                        'fill-rule': 'nonzero',
                      }),
                      h('path', {
                        d: 'M44.4235306,12.0484656 C44.4235306,13.4398207 45.464684,14.5880013 46.8103947,14.7564116 L47.1527411,14.7776761 C45.761386,14.7776761 44.6132054,15.8188294 44.4447951,17.1645402 L44.4235306,17.5068865 C44.4235306,16.1155315 43.3823773,14.9673508 42.0366665,14.7989405 L41.6943202,14.7776761 C43.0856752,14.7776761 44.2338559,13.7365227 44.4022662,12.390812 L44.4235306,12.0484656 Z',
                        fill: '#8860FC',
                        'fill-rule': 'nonzero',
                      }),
                      h('rect', {
                        fill: '#16204D',
                        x: '17.7398679',
                        y: '29.3178306',
                        width: '5.45842089',
                        height: '8.18763134',
                        rx: '2',
                      }),
                      h('rect', {
                        fill: '#16204D',
                        x: '28.6567097',
                        y: '29.3178306',
                        width: '5.45842089',
                        height: '8.18763134',
                        rx: '2',
                      }),
                      h('path', {
                        d: 'M33.2681489,12.9425679 C32.5347773,9.30362068 31.08981,7.48414704 28.9332468,7.48414704 C25.698402,7.48414704 25.7333802,11.8081925 28.9332468,11.4257275 C30.6212196,11.223972 31.7656965,11.7295855 32.3666775,12.9425679 L33.2681489,12.9425679 Z',
                        fill: g.ref(6),
                        transform: 'translate(29.8942, 10.2134) scale(-1, 1) translate(-29.8942, -10.2134)',
                      }),
                      h('path', {
                        d: 'M26.5202122,12.9425679 C25.7868406,9.30362068 24.3418733,7.48414704 22.1853101,7.48414704 C18.9504653,7.48414704 18.9854435,11.8081925 22.1853101,11.4257275 C23.8732829,11.223972 25.0177598,11.7295855 25.6187408,12.9425679 L26.5202122,12.9425679 Z',
                        fill: g.ref(6),
                      }),
                    ],
                  ),
                  h('path', {
                    d: 'M309,81.279814 C309,66.0493667 239.827994,53.7026496 154.5,53.7026496 C69.1720062,53.7026496 0,66.0493667 0,81.279814 C0,96.5102614 309,96.5102614 309,81.279814 Z',
                    fill: g.ref(7),
                    opacity: '0.22710455',
                  }),
                ],
              ),
            ],
          ),
        ],
      );
  },
});
